import { TopNav, TopNavHeading, TopNavItem } from '@astryxdesign/core/TopNav'
import { Section } from '@astryxdesign/core/Section'
import { Stack } from '@astryxdesign/core/Stack'
import { Grid } from '@astryxdesign/core/Grid'
import { Card } from '@astryxdesign/core/Card'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Link } from '@astryxdesign/core/Link'
import { Badge } from '@astryxdesign/core/Badge'
import { Divider } from '@astryxdesign/core/Divider'
import { Banner } from '@astryxdesign/core/Banner'
import StoreBadges from '../components/StoreBadges'
import AnimatedConversation from './landing/AnimatedConversation'
import { NAV, HERO, CAN_ASK, TRUST, SCREENS, DOWNLOAD, FOOTER } from './landing/content'

const APP_STORE_URL = "https://apps.apple.com/fr/app/padelr/id6657992480"
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.padel.day.padelr"

const LOGO = { width: '36px', height: '36px', borderRadius: '50%' }

// Centres a band's content without using Layout.
//
// Layout is an app shell: a header, a content region and a footer that each
// scroll independently, with the content region flexing to fill the frame.
// That is right for the admin console and wrong for a long marketing page -
// its content region stretched to the full page height, leaving ~390px of
// dead space above the footer. The percentage min-height it uses to do that
// resolves against its own rendered height here, so there is no ancestor to
// fix it from.
//
// A landing page wants ordinary document flow: bands stacked top to bottom,
// each centring its own content. That is all this is.
const BAND = { width: '100%', maxWidth: '1040px', marginInline: 'auto' }

function Band({ children }) {
  return <div style={BAND}>{children}</div>
}

// A real capture from the app.
//
// Borders and corners come from theme tokens rather than literals so the frame
// follows light and dark like everything else. No device chrome drawn around
// it: these are phone screenshots with their own status bar already in them,
// and a second bezel on top reads as a stock-photo mockup.
//
// `loading="lazy"` because both sit well below the fold, and every byte of
// this page ships inside the frontd binary.
const SHOT = {
  display: 'block',
  width: '100%',
  maxWidth: '280px',
  borderRadius: 'var(--radius-container)',
  border: '1px solid var(--color-border)',
}

function PhoneShot({ src, alt, caption, note }) {
  return (
    <Stack gap={3} hAlign="center">
      <img src={src} alt={alt} style={SHOT} loading="lazy" />
      <Stack gap={1} hAlign="center">
        <Text type="label">{caption}</Text>
        <Text type="supporting" justify="center">{note}</Text>
      </Stack>
    </Stack>
  )
}

// The front page.
//
// This is a bigger change than the design-system migration it sits on top of,
// and it is not what fancy-koala-2 was signed off to do. What it is, is the
// page [lucid-willow] asked for and [fancy-nimbus] planned - signed off in
// August and never built, because two sessions stopped before finishing it.
// So rather than invent a direction, this follows the one already agreed:
//
//   * [lively-jaguar] - the page leads with the assistant, not a feature
//     grid. The hero is a conversation. There is a section of real asks and a
//     panel saying the screens are all still there.
//   * [golden-cedar] - one page, built around real screenshots.
//
// Four deliberate departures from that plan. The first was decided in review;
// the rest are because the ground moved under it:
//
//   * lively-jaguar also asked for a section on what the assistant will not
//     do, given equal weight to what it will. That band is gone, replaced by
//     three shorter things a reader can count on - see the comment on TRUST
//     in content.js for why, and note that this reverses a signed-off
//     decision rather than drifting from one.
//
//   * It is built from Astryx components rather than the page's own scoped
//     stylesheet. fancy-nimbus specified one because Bulma could not express
//     the layout and the page needed a token system for two themes. Both of
//     those problems were solved by the migration this sits on: the token
//     system is the theme, and Bulma is gone.
//   * French only. `/en` and the strings map it needs are part of the agreed
//     plan and are not done; content.js is shaped so the English half is a
//     second key rather than a rewrite.
//   * Two screenshots, not the three lively-jaguar's revised list asked for
//     (the assistant mid-conversation, tournament search, the availability
//     calendar). What is shown is the assistant's opening screen and the
//     tournament calendar, and the captions say what they are rather than
//     what the list hoped for. The availability calendar exists and is not
//     used: it shows a real player, which is the same objection that took the
//     FFT ranking capture off this page - see the comment on SCREENS.shots.
export default function MainView() {
  return (
    <Stack gap={0}>
      <Section variant="transparent" padding={0} dividers={['bottom']}>
        <Band>
        <TopNav
          label="Navigation principale"
          heading={
            // `logo`, not `icon`. SideNavHeading takes `icon` and this one
            // does not, so the first version of this passed a prop nothing
            // reads and the mark simply did not appear - no warning, no error.
            <TopNavHeading
              heading="Padelr"
              logo={<img src="/img/logo-padelr-2048.png" alt="" style={LOGO} />}
              headingHref="/"
            />
          }
          endContent={
            <Stack direction="horizontal" gap={1} vAlign="center">
              <TopNavItem label={NAV.assistant} href="#assistant" />
              <TopNavItem label={NAV.features} href="#fonctionnalites" />
              <TopNavItem label={NAV.support} href="/support" />
            </Stack>
          }
        />
        </Band>
      </Section>

      <Band>
        <Stack gap={10} paddingBlock={8} paddingInline={4}>

          {/* Hero. Two columns on a wide screen, stacked on a narrow one -
              Grid's responsive form does that without a media query, and
              without the hero conversation being squeezed to a strip on a
              phone. */}
          <Section id="assistant" variant="transparent" padding={0}>
            <Grid columns={{ minWidth: 380, max: 2 }} gap={8} align="center">
              <Stack gap={5} hAlign="start">
                {/* In a horizontal Stack so it hugs its label. A Badge as a
                    direct child of a vertical Stack stretches to the column
                    width, which turned the eyebrow into a full-width filled
                    bar. */}
                <Stack direction="horizontal">
                  <Badge variant="info" label={HERO.eyebrow} />
                </Stack>
                <Heading level={1} type="display-2">{HERO.heading}</Heading>
                <Text as="p" type="large" color="secondary">{HERO.lede}</Text>
                <Stack gap={3}>
                  <StoreBadges
                    appStoreURL={APP_STORE_URL}
                    playStoreURL={PLAY_STORE_URL}
                  />
                  <Text type="supporting">{HERO.note}</Text>
                </Stack>
              </Stack>
              <AnimatedConversation />
            </Grid>
          </Section>

          <Divider />

          {/* What you can ask. Every item is a tool the agent really has. */}
          <Section id="fonctionnalites" variant="transparent" padding={0}>
            <Stack gap={6}>
              <Stack gap={3} maxWidth={720}>
                <Heading level={2}>{CAN_ASK.heading}</Heading>
                <Text as="p" type="large" color="secondary">{CAN_ASK.lede}</Text>
              </Stack>
              <Grid columns={{ minWidth: 280, max: 3 }} gap={4}>
                {CAN_ASK.items.map((item) => (
                  <Card key={item.title} padding={5}>
                    <Stack gap={3}>
                      <Heading level={3}>{item.title}</Heading>
                      <Text as="p" type="label" color="accent">{item.example}</Text>
                      <Text as="p" color="secondary">{item.body}</Text>
                    </Stack>
                  </Card>
                ))}
              </Grid>
            </Stack>
          </Section>

          {/* Three things a reader can count on, not four things the
              assistant will not do - see the comment on TRUST. Lighter than
              the band it replaces: no muted background, three columns rather
              than a two-by-two block, and it sits with the page rather than
              interrupting it. */}
          <Section variant="muted" padding={8}>
            <Stack gap={6}>
              <Stack gap={3} maxWidth={720}>
                <Heading level={2}>{TRUST.heading}</Heading>
                <Text as="p" type="large" color="secondary">{TRUST.lede}</Text>
              </Stack>
              <Grid columns={{ minWidth: 260, max: 3 }} gap={6}>
                {TRUST.items.map((item) => (
                  <Stack key={item.title} gap={2}>
                    <Heading level={3}>{item.title}</Heading>
                    <Text as="p" color="secondary">{item.body}</Text>
                  </Stack>
                ))}
              </Grid>
              {/* The app shows this before a player types anything, so the
                  page cannot be more confident than the product is. Last in
                  the section rather than first: it is a caveat, not the
                  headline. */}
              <Banner status="warning" title={TRUST.beta} />
            </Stack>
          </Section>

          {/* The assistant is a way in, not a toll gate. */}
          <Section variant="transparent" padding={0}>
            <Stack gap={6}>
              <Stack gap={3} maxWidth={720}>
                <Heading level={2}>{SCREENS.heading}</Heading>
                <Text as="p" type="large" color="secondary">{SCREENS.body}</Text>
              </Stack>

              {/* Real captures, not mockups. */}
              <Grid columns={{ minWidth: 280, max: 2 }} gap={8}>
                {SCREENS.shots.map((shot) => (
                  <PhoneShot key={shot.src} {...shot} />
                ))}
              </Grid>

              {/* `max: 2`, where the other grids on this page are 3.
                  Court booking made this four cards, and four does not fit
                  across a 1040px band at this minimum width: measured in a
                  browser, `max: 4` lays out 3 + 1 and leaves the newest card
                  stranded on its own row looking like an afterthought. 2 x 2
                  reads as a choice. */}
              <Grid columns={{ minWidth: 260, max: 2 }} gap={4}>
                {SCREENS.areas.map((area) => (
                  <Card key={area.title} padding={5}>
                    <Stack gap={2}>
                      <Heading level={3}>{area.title}</Heading>
                      <Text as="p" color="secondary">{area.body}</Text>
                      {/* Only the booking card has one: what it says is
                          available to whom. Supporting text rather than part
                          of the body, so it reads as the caveat it is. */}
                      {area.note && <Text type="supporting">{area.note}</Text>}
                    </Stack>
                  </Card>
                ))}
              </Grid>
            </Stack>
          </Section>

          <Section id="telecharger" variant="section" padding={8}>
            <Stack gap={5} hAlign="center">
              <Heading level={2} justify="center">{DOWNLOAD.heading}</Heading>
              <Text as="p" type="large" color="secondary" justify="center">
                {DOWNLOAD.body}
              </Text>
              <StoreBadges
                appStoreURL={APP_STORE_URL}
                playStoreURL={PLAY_STORE_URL}
              />
            </Stack>
          </Section>

        </Stack>
      </Band>

      <Section variant="transparent" padding={0} dividers={['top']}>
        <Band>
        <Stack gap={6} paddingBlock={8} paddingInline={4}>
          <Grid columns={{ minWidth: 220, max: 4 }} gap={6}>
            <Stack gap={3}>
              <Stack direction="horizontal" gap={2} vAlign="center">
                <img src="/img/logo-padelr-2048.png" alt="" style={LOGO} />
                <Text weight="semibold">Padelr</Text>
              </Stack>
              <Text type="supporting">{FOOTER.tagline}</Text>
            </Stack>
            {FOOTER.columns.map((col) => (
              <Stack key={col.title} gap={3}>
                <Text type="label" color="secondary">{col.title}</Text>
                <Stack gap={2}>
                  {col.links.map((l) => (
                    <Link key={l.label} href={l.href}>{l.label}</Link>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Grid>
          <Divider />
          <Text type="supporting">© Padelr</Text>
        </Stack>
        </Band>
      </Section>
    </Stack>
  )
}
